import { LIBRARY_SLUG_PATTERN } from "./library/library-rights.js";
import { isLibraryInstanceSlug } from "./library/library-instance-slug.js";
import { firstTopicToken, isLibraryEventTopic } from "./library/library-topics.js";
import { compileNatsGrantSubjects } from "./stream-verbs.js";
import { compileStandingGrants } from "./nats-standing-grants.js";
//#region src/event-subjects.ts
/**
* Shared NATS naming + durable-consumer contract (cold-start-3 event durability).
*
* One derivation, owned by the zero-dep `@zion/sdk-contracts` leaf, used by BOTH
* the operator (KEDA trigger + pre-seed) and the SDK runner / claim-pod handler
* — so the durable name, stream name, and consumer filter can never drift across
* two formulas (the load-bearing repair, §4.2 of the PR3 plan).
*
* **Names are dot-FREE; subjects keep dots.** NATS JetStream durable/consumer
* names may NOT contain `.` / `*` / `>` / whitespace, so every name component is
* pushed through {@link sanitizeForDurableName} and then asserted NATS-legal via
* {@link assertValidDurableName}. Subjects are token-delimited and keep their dots.
*
* **Version-shared durables ⇒ version-agnostic WILDCARD filter.** A version-shared
* durable load-balances one logical worker across all active versions. JetStream
* consumer `filter_subject`s are immutable, so the consumer filter is the
* version-agnostic wildcard (`zion.<app>.<topic>.>`) — DISTINCT from the
* version-suffixed *publish* subject (`zion.<app>.<topic>.<version>`). A second
* version's idempotent ensure is then a clean no-op, not a filter-change error.
*
* Pure, zero runtime dependencies — no `nats` import. The adaptation of
* {@link WORKER_CONSUMER_CONFIG} into the `nats` `ConsumerConfig` (seconds →
* nanoseconds, `AckPolicy.Explicit`) happens at the call sites in `@zion/sdk-node`.
*/
/** NATS durable/consumer names may not contain `.`, `*`, `>`, or whitespace. */
const ILLEGAL_DURABLE_NAME_CHAR = /[.*>\s]/;
/**
* A NATS subject TOPIC: one or more dot-delimited tokens of `[a-zA-Z0-9_-]`
* (e.g. `indexing`, `document.created`). No `*`, `>`, or whitespace; non-empty.
*
* §0.3 (relaxed from single-token): multi-segment `domain.event` topics are the
* platform convention. The durability layer is dot-safe — {@link sanitizeForDurableName}
* makes the durable name dot-free and the `zion.<app>.<topic>.>` filter keeps
* its dots — so a dotted topic is well-defined end to end.
*/
const TOPIC_PATTERN = /^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)*$/;
/**
* The reserved prefix a topic must NOT start with. A topic is RELATIVE to its
* app scope; one that is already a FULL subject (`zion.…`) is the §0.3
* anti-pattern that would derive `zion.<app>.zion.docs.indexing.>`. Matched
* case-insensitively.
*/
const RESERVED_FULL_SUBJECT_PREFIX = /^zion\./i;
/**
* The JetStream API DOMAIN the DP hub runs under (chart `nats.jetstreamDomain`, D6/D7).
* A configured domain disambiguates the hub's JetStream API subject namespace: a
* leaf-connected client addresses it via `$JS.<domain>.API.*` (a plain `$JS.API` would
* hit the leaf's OWN `leaf`-domain JS). Directly-connected clients keep using plain
* `$JS.API.*` (the domain ADDS the disambiguated alias, it does not remove the
* original). SINGLE SOURCE for the grant helpers below AND the SDK `apiPrefix` value —
* the subjects a cred is GRANTED and the subjects an SDK client PUBLISHES derive from
* THIS one constant so they can never drift (AD-6).
*/
const NATS_JS_API_DOMAIN = "hub";
/** The domain-qualified JetStream API subject PREFIX (`$JS.hub.API`). */
const NATS_JS_API_PREFIX = `$JS.hub.API`;
/**
* The env-var NAME the operator stamps on leaf-bearing app/worker containers and the
* SDKs read to obtain the nats.js `apiPrefix`. Value is {@link NATS_JS_API_PREFIX}
* (`$JS.hub.API`); UNSET → no prefix (a directly-connected client / local-no-domain dev,
* which uses plain `$JS.API`).
*/
const ZION_NATS_JS_API_PREFIX_ENV = "ZION_NATS_JS_API_PREFIX";
/**
* The env-var NAME the operator stamps on leaf-bearing app containers carrying the
* DP HUB's direct NATS URL (`nats://nats.<ns>.svc:<port>`) — incident 2026-07-02
* (per-EC creds). A leaf-bearing pod's `NATS_URL` points at the LOOPBACK leaf,
* whose upstream link is authenticated as the APP cred — so a connection that
* must authenticate as a DIFFERENT principal (an agent execution-context with its
* per-EC `.creds`) is meaningless through the leaf: the hub only ever sees the
* app cred, and unpermitted subjects are silently dropped at the leaf remote
* (no violation log, no responder → NatsError 503). Per-EC connections MUST dial
* this hub URL directly. UNSET → older operator / no leaf: per-EC wiring falls
* back gracefully (shared service creds).
*/
const ZION_NATS_HUB_URL_ENV = "ZION_NATS_HUB_URL";
/**
* Build a domain-qualified JetStream API subject: `$JS.hub.API.<suffix>`. Exported for the verb
* compiler in `stream-verbs.ts`; the package barrel does not export it.
*/
function jsApi(suffix) {
	return `${NATS_JS_API_PREFIX}.${suffix}`;
}
/**
* The PLAIN (un-qualified) JetStream API subject PREFIX (`$JS.API`). A
* DIRECT-to-hub client (NO `apiPrefix`) addresses JetStream via plain `$JS.API`
* for ALL JS ops — consumer-API AND KV — so its grant must be plain (D14). The
* domain-qualified {@link NATS_JS_API_PREFIX} is LEAF-only.
*/
const NATS_JS_API_PLAIN_PREFIX = "$JS.API";
/**
* Build a PLAIN JetStream API subject: `$JS.API.<suffix>` (D14 — direct svc). Exported for the verb
* compiler in `stream-verbs.ts`; the package barrel does not export it.
*/
function jsApiPlain(suffix) {
	return `${NATS_JS_API_PLAIN_PREFIX}.${suffix}`;
}
/**
* BOTH JetStream-API dialects for one suffix — qualified `$JS.hub.API.<suffix>`
* (leaf-routed) AND plain `$JS.API.<suffix>` (direct-to-hub). Incident 2026-07-02:
* the per-EC AGENT cred connects DIRECT-to-hub (its `ZION_NATS_HUB_URL` connection,
* bypassing the leaf), and nats.js emits PLAIN `$JS.API.*` there (verified on the
* wire), but the same agent-kind grant must also work for any op nats.js prefixes /
* a leaf-routed caller. Granting both is same-resource, both-routing-form (the hub
* serves both) — it makes the agent grant robust to the connection dialect instead
* of chasing one denied subject per deploy. Used ONLY by the AGENT branch of
* {@link agentConsumerGrantSubjects} + {@link agentLeaseKvGrantSubjects}; the svc
* branch (plain, direct) and the sibling grant (qualified, leaf) are unchanged. Exported for the verb
* compiler in `stream-verbs.ts`, whose `dual` dialect it renders; the package barrel does not export it.
*/
function dualJsApi(suffix) {
	return [jsApi(suffix), jsApiPlain(suffix)];
}
/**
* Sanitize a NATS subject token for use inside a JetStream durable consumer
* name. The on-the-wire subject keeps wildcards (`*`, `>`) intact, but a
* durable name must be a stable identifier, so we substitute collision-free
* aliases:
*   `*` → `_all`, `>` → `_gt`, `.` → `-`.
*
* (Moved here from `nats-worker-runner.ts` so both the operator pre-seed and the
* SDK runner / durable subscriber import a single implementation.)
*/
function sanitizeForDurableName(token) {
	return token.replace(/\./g, "-").replace(/\*/g, "_all").replace(/>/g, "_gt");
}
/**
* §0.3 — assert a worker/event `topic` is a valid relative NATS topic: one or
* more dot-delimited tokens, no wildcards/whitespace, and NOT already a `zion.`
* full subject. The leading-`zion.` guard is the real §0.3 anti-pattern (it
* would derive `zion.<app>.zion.docs.indexing.>`); multi-segment `domain.event`
* topics are accepted.
*/
function assertValidTopic(topic) {
	if (!TOPIC_PATTERN.test(topic)) throw new Error(`Invalid event topic ${JSON.stringify(topic)}: must be one or more dot-delimited NATS tokens matching /^[a-zA-Z0-9_-]+(\\.[a-zA-Z0-9_-]+)*$/ (no wildcards or whitespace).`);
	if (RESERVED_FULL_SUBJECT_PREFIX.test(topic)) throw new Error(`Invalid event topic ${JSON.stringify(topic)}: must not start with the reserved 'zion.' prefix — a topic is relative to its app scope, not a full subject.`);
}
/**
* B1 — assert a NATS durable/consumer name is legal: no `.` / `*` / `>` /
* whitespace. Used inside the name builders AND asserted by the wiring-integrity
* keystone test so a dotted name can never survive the test but fail
* `consumers.add` at runtime.
*/
function assertValidDurableName(name) {
	if (ILLEGAL_DURABLE_NAME_CHAR.test(name)) throw new Error(`Invalid NATS durable name ${JSON.stringify(name)}: must not contain '.', '*', '>', or whitespace.`);
}
/** The JetStream stream name for an app scope: `ZION_<APPSCOPE>`. */
function eventStreamName(appScope) {
	return `ZION_${appScope.toUpperCase()}`;
}
/**
* The bounded JetStream PUBLISH grant an app principal needs to operate as a
* JetStream client against its OWN stream `ZION_<APPSCOPE>` (nats-app-jetstream-
* consumption AD-2). Exact-match admitted by core-api's mint gate
* (`isNatsSubjectWithinAppScope`) and composed into the operator's per-app mint
* request — both sides import THIS list, never literals.
*
*   - `CONSUMER.CREATE` (new API, nats.js vs server ≥ 2.9 — the publish subject
*     embeds `<durable>.<filter>`, covered by the `.>` tail) and
*     `CONSUMER.DURABLE.CREATE` (legacy API — @zion/sdk-python) are BOTH
*     required: the two SDKs publish different consumer-create subjects.
*   - CONSUMER.INFO / CONSUMER.MSG.NEXT / `$JS.ACK` are the pull-consumer
*     minimum (consumers.get / consume / ack).
*
* Deliberately ABSENT: `$JS.API.INFO` + `$JS.API.STREAM.CREATE.<stream>`
* (the SDK ≤ 7.0 compat grants — REMOVED by S8 / interactive-RPC AD-11 once
* the fleet reached ≥ 7.1: SDK ≥ 7.1 skips the jetstreamManager INFO probe
* via `checkAPI: false` and never ensure-creates streams; the operator is the
* sole stream author), STREAM.INFO on THIS OWN stream (no app-credentialed code
* path ordered-consumes the app's own `ZION_<app>` stream — the CONSUMER.* pull
* path needs no STREAM.INFO; an EPHEMERAL/ORDERED bind on a SIBLING per-principal
* stream DOES, and the `nats_stream:read` verb of `stream-verbs.ts` grants STREAM.INFO there),
* STREAM UPDATE/DELETE/PURGE, any other stream's name, `$JS.API.>` —
* HR-1 (app principals never hold stream admin or cross-app reach).
*/
function appJetStreamGrantSubjects(appScope) {
	const stream = eventStreamName(appScope);
	return [
		jsApi(`CONSUMER.CREATE.${stream}.>`),
		jsApi(`CONSUMER.DURABLE.CREATE.${stream}.>`),
		jsApi(`CONSUMER.INFO.${stream}.>`),
		jsApi(`CONSUMER.MSG.NEXT.${stream}.>`),
		`$JS.ACK.${stream}.>`
	];
}
/**
* The version-shared (OQ-2), dot-FREE (B1) worker durable name:
* `<appScope>-<sanitize(topic)>`. Drops the runner's old `-<version>` suffix and
* the operator's un-derivable `fixtureName/workerName` triple — both ends now
* compute the same name from `(appScope, topic)`.
*/
function workerDurableName(appScope, topic) {
	assertValidTopic(topic);
	const name = `${appScope}-${sanitizeForDurableName(topic)}`;
	assertValidDurableName(name);
	return name;
}
/**
* The version-agnostic WILDCARD consumer filter for a worker durable:
* `zion.<appScope>.<topic>.>` (OQ-2 — immutable-filter safe across versions).
*/
function workerFilterSubject(appScope, topic) {
	assertValidTopic(topic);
	return `zion.${appScope}.${topic}.>`;
}
/**
* The version-suffixed publish subject: `zion.<appScope>.<topic>.<version>`.
* The wildcard {@link workerFilterSubject} consumer receives every version
* published here.
*/
function eventPublishSubject(appScope, topic, version) {
	assertValidTopic(topic);
	return `zion.${appScope}.${topic}.${version}`;
}
/** The second token of an instance's inbound tree: `zion.<instance>.in.<topic>.<version>`. */
const LIBRARY_INBOUND_SUBJECT_TOKEN = "in";
/** The second token of a consuming app's outbound tree: `zion.<consumer>.lib.<library>.<topic>.<version>`. */
const LIBRARY_OUTBOUND_SUBJECT_TOKEN = "lib";
/** Throws `Error` when `topic` is no library event topic. */
function assertLibraryTopic(topic) {
	if (!isLibraryEventTopic(topic)) throw new Error(`Invalid library event topic ${JSON.stringify(topic)}: must match /^[a-z][a-z0-9_-]{0,62}$/ and not be 'in'.`);
}
/** Throws `Error` when `slug` is no instance slug. */
function assertInstanceScope(slug) {
	if (!isLibraryInstanceSlug(slug)) throw new Error(`Invalid library instance slug ${JSON.stringify(slug)}.`);
}
/**
* The grammar of a consumer slug in a library subject: the `appId` grammar of
* `app-manifest.schema.json`, at most 30 characters. A consuming app may have a `--` in its slug;
* a library may not ({@link LIBRARY_SLUG_PATTERN}).
*/
const CONSUMER_SLUG_PATTERN = /^[a-z][a-z0-9-]*[a-z0-9]$/;
/** Throws `Error` when `slug` is no app slug of at most 30 characters. */
function assertConsumerSlug(slug) {
	if (slug.length > 30 || !CONSUMER_SLUG_PATTERN.test(slug)) throw new Error(`Invalid consumer slug ${JSON.stringify(slug)}.`);
}
/** Throws `Error` when `slug` is no library slug of at most 30 characters. */
function assertLibrarySlug(slug) {
	if (slug.length > 30 || !LIBRARY_SLUG_PATTERN.test(slug)) throw new Error(`Invalid library slug ${JSON.stringify(slug)}.`);
}
/**
* The inbound publish subject: `zion.<instance>.in.<topic>.<version>`. The consuming app publishes
* here; the instance's stream `ZION_<INSTANCE>` captures it. It throws `Error` for a bad instance
* slug or topic. `version` is not checked, as in {@link eventPublishSubject}. Pure.
*/
function libraryInboundPublishSubject(instanceSlug, topic, version) {
	assertInstanceScope(instanceSlug);
	assertLibraryTopic(topic);
	return `zion.${instanceSlug}.in.${topic}.${version}`;
}
/**
* The version-agnostic inbound subject `zion.<instance>.in.<topic>.>`: the instance's consumer
* filter for the topic, and the publish permission core-api compiles for the consuming app. It
* throws `Error` for a bad instance slug or topic. Pure.
*/
function libraryInboundFilterSubject(instanceSlug, topic) {
	assertInstanceScope(instanceSlug);
	assertLibraryTopic(topic);
	return `zion.${instanceSlug}.in.${topic}.>`;
}
/**
* The outbound publish subject: `zion.<consumer>.lib.<library>.<topic>.<version>`. The instance
* publishes here; the consuming app's stream captures it. It throws `Error` for a bad consumer slug,
* library slug or topic. Pure.
*/
function libraryOutboundPublishSubject(consumerSlug, librarySlug, topic, version) {
	assertConsumerSlug(consumerSlug);
	assertLibrarySlug(librarySlug);
	assertLibraryTopic(topic);
	return `zion.${consumerSlug}.lib.${librarySlug}.${topic}.${version}`;
}
/**
* The version-agnostic outbound subject `zion.<consumer>.lib.<library>.<topic>.>`: the consuming
* app's consumer filter for a binding on that topic, and the publish permission core-api compiles
* for the instance. It throws `Error` for a bad consumer slug, library slug or topic. Pure.
*/
function libraryOutboundFilterSubject(consumerSlug, librarySlug, topic) {
	assertConsumerSlug(consumerSlug);
	assertLibrarySlug(librarySlug);
	assertLibraryTopic(topic);
	return `zion.${consumerSlug}.lib.${librarySlug}.${topic}.>`;
}
/**
* The refusal of an app's own event topic, or `null` (R-030, final-3). It reads the topic only; the
* per-instance key that a publish puts before the topic is {@link perInstanceKeyRefusal}'s. The checks
* run in this order:
*
* 1. `invalid`: the topic fails {@link assertValidTopic} (the dotted token grammar, or a `zion.` prefix);
* 2. `reserved_lib`: the first token is `lib`, for every app;
* 3. `reserved_in`: the first token is `in`, for an app of kind `library`.
*
* A binding `on.event` that names a library outbound topic is no own topic: read it with
* {@link parseLibraryOutboundBindingTopic} first. Never throws. Pure.
*/
function appTopicRefusal(topic, kind) {
	try {
		assertValidTopic(topic);
	} catch {
		return "invalid";
	}
	const first = firstTopicToken(topic);
	if (first === "lib") return "reserved_lib";
	if (kind === "library" && first === "in") return "reserved_in";
	return null;
}
/**
* The refusal of a per-instance key, or `null` (WS-0-R8, R-030). A per-instance key is the
* `PublishOptions.instanceId` of an app's publish and the `instanceKey` of a per-instance claim. The
* publisher writes it as the token after the app scope: `zion.<app>.<instanceKey>.<topic>.<version>`.
* That is the same subject position as the token `lib` of a consuming app's outbound tree and the
* token `in` of an instance's inbound tree, so the key `lib` puts the app's own per-instance events
* into {@link libraryOutboundFilterSubject}, and the key `in` puts them into
* {@link libraryInboundFilterSubject}. The checks run in this order on the first dot-delimited token
* of the key:
*
* 1. `reserved_lib`: the first token is {@link LIBRARY_OUTBOUND_SUBJECT_TOKEN} (`lib`);
* 2. `reserved_in`: the first token is {@link LIBRARY_INBOUND_SUBJECT_TOKEN} (`in`).
*
* Both tokens are refused for every app kind: the claim door and the publisher do not read the app
* kind, and refuse over accept (DEF-3). The match is exact, as NATS tokens are: `LIB`, `library` and
* `inbox` pass. The function reads no other grammar: the empty key, which the publisher writes as no
* segment, passes, and the door keeps its own character check (`ID_PART_PATTERN`). The per-instance
* subject builders of this module throw on a refused key. Never throws. Pure.
*/
function perInstanceKeyRefusal(instanceKey) {
	const first = firstTopicToken(instanceKey);
	if (first === "lib") return "reserved_lib";
	if (first === "in") return "reserved_in";
	return null;
}
/** Throws `Error` when {@link perInstanceKeyRefusal} refuses `instanceKey`. */
function assertPerInstanceKey(instanceKey) {
	const refusal = perInstanceKeyRefusal(instanceKey);
	if (refusal !== null) throw new Error(`Invalid per-instance key ${JSON.stringify(instanceKey)}: ${refusal}, the first token is reserved for the library subject trees.`);
}
/** The longest binding topic {@link parseLibraryOutboundBindingTopic} reads: `lib.`, a slug, `.` and a topic. */
const LIBRARY_BINDING_TOPIC_MAX_LENGTH = 98;
/**
* The library and topic of a consuming app's binding `on.event` of the form
* `lib.<library-slug>.<topic>` (R-024 item 4, R-026), or `null` for any other text. The bridge
* composes `zion.<consumer>.lib.<library-slug>.<topic>.>` from it, which is
* {@link libraryOutboundFilterSubject}. The publish door also checks that the app version uses the
* library and that the topic is an outbound topic of it. Never throws. Pure.
*/
function parseLibraryOutboundBindingTopic(topic) {
	if (topic.length > LIBRARY_BINDING_TOPIC_MAX_LENGTH) return null;
	const parts = topic.split(".");
	if (parts.length !== 3 || parts[0] !== "lib") return null;
	const [, librarySlug, libraryTopic] = parts;
	if (librarySlug.length > 30 || !LIBRARY_SLUG_PATTERN.test(librarySlug)) return null;
	if (!isLibraryEventTopic(libraryTopic)) return null;
	return {
		librarySlug,
		topic: libraryTopic
	};
}
/**
* The dot-FREE (B1) per-instance command durable name:
* `<appId>-<fixtureId>-<instanceKey>-cmd`. Each part is sanitized and the result
* asserted NATS-legal.
*/
function commandDurableName({ appId, fixtureId, instanceKey }) {
	const name = `${sanitizeForDurableName(appId)}-${sanitizeForDurableName(fixtureId)}-${sanitizeForDurableName(instanceKey)}-cmd`;
	assertValidDurableName(name);
	return name;
}
/**
* The per-instance command PUBLISH subject (version-suffixed):
* `zion.<appScope>.<instanceKey>.cmd.<version>`. This is exactly what the
* launcher's `PublishOptions.instanceId` path produces for topic `cmd`.
* It throws `Error` for a key that {@link perInstanceKeyRefusal} refuses
* (`lib` or `in` as the first token, WS-0-R8).
*/
function commandSubject({ appScope, instanceKey, version }) {
	assertPerInstanceKey(instanceKey);
	return `zion.${appScope}.${instanceKey}.cmd.${version}`;
}
/**
* The per-instance command consumer filter + pre-seed subject (version-agnostic
* WILDCARD): `zion.<appScope>.<instanceKey>.cmd.>`. Immutable-filter safe across
* versions, mirroring the worker filter/publish divergence. It throws `Error`
* for a key that {@link perInstanceKeyRefusal} refuses (WS-0-R8).
*/
function commandFilterSubject({ appScope, instanceKey }) {
	assertPerInstanceKey(instanceKey);
	return `zion.${appScope}.${instanceKey}.cmd.>`;
}
/**
* The per-instance rpc PUBLISH subject (version-suffixed):
* `zion.<appScope>.<instanceKey>.rpc.<version>`. This is exactly what the
* launcher's `PublishOptions.instanceId` path produces for topic `rpc`.
* It throws `Error` for a key that {@link perInstanceKeyRefusal} refuses
* (WS-0-R8).
*/
function rpcSubject({ appScope, instanceKey, version }) {
	assertPerInstanceKey(instanceKey);
	return `zion.${appScope}.${instanceKey}.rpc.${version}`;
}
/**
* The per-instance rpc consumer filter + pre-seed subject (version-agnostic
* WILDCARD): `zion.<appScope>.<instanceKey>.rpc.>`. Immutable-filter safe across
* versions, mirroring the worker filter/publish divergence. It throws `Error`
* for a key that {@link perInstanceKeyRefusal} refuses (WS-0-R8).
*/
function rpcFilterSubject({ appScope, instanceKey }) {
	assertPerInstanceKey(instanceKey);
	return `zion.${appScope}.${instanceKey}.rpc.>`;
}
/**
* The principal KINDs an agent stream can belong to. `agent` streams retain a
* shorter window than the longer-lived human and service-account streams
* (see {@link AGENT_STREAM_RETENTION_POLICY}).
*
* A team is a group and never a principal, so it owns no stream (dp-resource-grants,
* round 1 rows 1 and 3). A team reaches a stream through a `nats_stream:read`
* grant on the stream's principal, which the credential compile turns into consumer
* subjects. `isAgentStreamKind('team')` answers `false`, and the operator broker
* refuses a `team` request as `invalid-kind`.
*/
const AGENT_STREAM_KINDS = [
	"agent",
	"human",
	"svc"
];
/** True iff `k` is a known {@link AgentStreamKind}. */
function isAgentStreamKind(k) {
	return AGENT_STREAM_KINDS.includes(k);
}
/**
* `orgId` as a NATS subject + stream-name token: lowercase alnum / `-`, 1–64
* chars. These names/subjects are SERVER-derived, never trusted — a non-matching
* orgId is rejected by {@link assertValidStreamOrgId} (fail-closed; it could
* otherwise smuggle a `.`/`*`/`>` into the derived subject or stream name).
*/
const STREAM_ORG_ID_PATTERN = /^[a-z0-9-]{1,64}$/;
/**
* `principalId` as a NATS subject + stream-name token: lowercase alnum / `-`, 1–40 chars.
*
* EXPORTED (cu-hosted-principal-stream-grants S-1) so a WIRE schema can bind the SAME grammar
* {@link assertValidStreamPrincipalId} enforces at subject-build time, instead of hand-copying the
* literal a fourth time (core-api's `consumesStreamPrincipals`/`publishStreamPrincipals` DTOs already
* carry three copies). Exporting the pattern does not relax the assert — the assert remains the
* fail-closed last line at subject-build time; the wire schema is the fail-EARLY first line.
*/
const STREAM_PRINCIPAL_ID_TOKEN_PATTERN = /^[a-z0-9-]{1,40}$/;
/**
* Assert `orgId` is a legal agent-stream org token (`^[a-z0-9-]{1,64}$`). These
* identifiers are SERVER-derived into stream names and forced subjects, never
* trusted from the requester; a malformed value throws rather than producing a
* degenerate / wildcard-smuggling subject. Exported for direct branch testing.
*/
function assertValidStreamOrgId(orgId) {
	if (!STREAM_ORG_ID_PATTERN.test(orgId)) throw new Error(`Invalid agent-stream orgId ${JSON.stringify(orgId)}: must match /^[a-z0-9-]{1,64}$/.`);
}
/**
* Assert `principalId` is a legal agent-stream principal token
* (`^[a-z0-9-]{1,40}$`). Same SERVER-derived, never-trusted rationale as
* {@link assertValidStreamOrgId}. Exported for direct branch testing.
*/
function assertValidStreamPrincipalId(principalId) {
	if (!STREAM_PRINCIPAL_ID_TOKEN_PATTERN.test(principalId)) throw new Error(`Invalid agent-stream principalId ${JSON.stringify(principalId)}: must match /^[a-z0-9-]{1,40}$/.`);
}
/**
* The SHAPE a broker-resolvable stream principal id must take (M5,
* self-healing-reconciler §11): EITHER a standard lowercase-hex UUID (any
* machine principal — agent / worker / service) OR the human `user-<ulid>`
* directory id — `user-` followed by a 26-char lowercase-alnum ULID, the
* canonical human stream slug. SINGLE SOURCE for the operator broker (its former
* ad-hoc `UUID_RE`) and core-api (`principalOrgParamSchema`) so the two shape
* gates can never drift.
*
* This is the SHAPE gate a consumer applies to a principal id BEFORE deriving a
* stream; it COMPLEMENTS — never replaces — {@link assertValidStreamPrincipalId}
* (the fail-closed subject-injection TOKEN guard). Every value that passes THIS
* matcher is also token-legal (a UUID and a `user-<ulid>` both match
* `/^[a-z0-9-]{1,40}$/`), but the injection guard remains the last line at
* subject-build time. Anchored + no wildcard/dot/whitespace admitted, so a
* subject-injection attempt (dots, `>`, `*`, spaces) is rejected here too.
*/
const STREAM_PRINCIPAL_ID_PATTERN = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|user-[0-9a-z]{26})$/;
/**
* True iff `id` is a well-shaped stream principal id — a standard UUID or a
* `user-<ulid>` human id (see {@link STREAM_PRINCIPAL_ID_PATTERN}). The SHAPE
* gate the operator broker + core-api call before deriving a per-principal
* stream; complements (does not replace) {@link assertValidStreamPrincipalId}.
*/
function isValidStreamPrincipalId(id) {
	return STREAM_PRINCIPAL_ID_PATTERN.test(id);
}
/**
* The SERVER-derived per-principal JetStream stream name: `S_<orgId>_<principalId>`.
* Both identifiers are asserted token-legal first (fail-closed) so the name is
* NATS-legal by construction. The `V_<org>_*` team-view streams (Phase 2) share
* the consumer grant shape but are not derived here.
*/
function agentStreamName(orgId, principalId) {
	assertValidStreamOrgId(orgId);
	assertValidStreamPrincipalId(principalId);
	return `S_${orgId}_${principalId}`;
}
/**
* The SERVER-FORCED subjects the per-principal stream captures:
* `['org.<orgId>.p.<kind>.<principalId>.>']` — the principal's OWN subtree, the
* `.>` tail covering its inbox facet (`…inbox.>`) and everything else under it.
* One element by design; a list keeps the {@link StreamSpec.subjects} shape.
*/
function agentStreamSubjects(orgId, kind, principalId) {
	assertValidStreamOrgId(orgId);
	assertValidStreamPrincipalId(principalId);
	return [`org.${orgId}.p.${kind}.${principalId}.>`];
}
const DAY_SECONDS = 86400;
const TWO_MINUTES_SECONDS = 120;
/**
* Per-kind retention floors (§2 settled decision): `agent` streams keep 14 days,
* the longer-lived `human` / `svc` streams keep 30 days; all share a
* 2-minute de-duplication window. {@link clampRetention} floors a requested
* value UP to these policy minimums — a requester can ask for MORE retention but
* never less than the platform floor.
*/
const AGENT_STREAM_RETENTION_POLICY = {
	agent: {
		maxAgeSeconds: 14 * DAY_SECONDS,
		duplicateWindowSeconds: TWO_MINUTES_SECONDS
	},
	human: {
		maxAgeSeconds: 30 * DAY_SECONDS,
		duplicateWindowSeconds: TWO_MINUTES_SECONDS
	},
	svc: {
		maxAgeSeconds: 30 * DAY_SECONDS,
		duplicateWindowSeconds: TWO_MINUTES_SECONDS
	}
};
/**
* Clamp a requested retention to the per-kind policy FLOOR: each field is the
* MAX of the requested value and the policy minimum, so a request can lengthen
* retention but never shorten it below the platform floor (§2). A requested
* field left `undefined` defaults to the policy floor.
*/
function clampRetention(kind, requested = {}) {
	const policy = AGENT_STREAM_RETENTION_POLICY[kind];
	return {
		maxAgeSeconds: Math.max(policy.maxAgeSeconds, requested.maxAgeSeconds ?? policy.maxAgeSeconds),
		duplicateWindowSeconds: Math.max(policy.duplicateWindowSeconds, requested.duplicateWindowSeconds ?? policy.duplicateWindowSeconds)
	};
}
/**
* The PUBLISH grant for a per-principal NATS connection (dynamic-agent-streams
* §3, the mailbox cross-subtree case). Two globs, BOTH under `org.<org>.>` so
* they pass `isNatsSubjectWithinOrgScope` (the existing org bound):
*
*   1. own subtree — `org.<org>.p.<kind>.<id>.>` (publish into its own stream).
*   2. org inbox   — `org.<org>.p.*.*.inbox.>` (cross-post into a peer's inbox
*      facet in the SAME org). **WRITE-ONLY: this entry must NEVER appear in a
*      subscribe allow-list** — the `*.*` reaches the `.inbox.>` facet of every
*      peer, not peers' full subtrees, and only because it is publish-only. The
*      team request subjects `org.<org>.rq.team.<slug>.>` of
*      `nats_request_endpoint` sit outside `org.<org>.p.>` (R-042), so neither glob
*      sends a request; a `call` row does.
*
* Mirrors the EXACT-string-match composition style of
* {@link appJetStreamGrantSubjects}: the operator mint AND the provision
* validation import THIS list, never literals.
*/
function agentPublishSubjects(orgId, kind, principalId) {
	assertValidStreamOrgId(orgId);
	assertValidStreamPrincipalId(principalId);
	return [`org.${orgId}.p.${kind}.${principalId}.>`, `org.${orgId}.p.*.*.inbox.>`];
}
/**
* The SUBSCRIBE grant for a per-principal NATS connection: ONLY the principal's
* own subtree `org.<org>.p.<kind>.<id>.>`. Deliberately does NOT include the
* org-inbox glob from {@link agentPublishSubjects} (that one is write-only) — a
* principal may cross-post into peers' inboxes but may not subscribe to them.
*/
function agentSubscribeSubjects(orgId, kind, principalId) {
	assertValidStreamOrgId(orgId);
	assertValidStreamPrincipalId(principalId);
	return [`org.${orgId}.p.${kind}.${principalId}.>`];
}
/**
* Assert every NATS wildcard token in `subject` is whole-token-legal: a `*` or
* `>` may appear ONLY as a COMPLETE token between dots (or at the start/end),
* never embedded inside a token (`S_acme_*` is an EMBEDDED `*` — ILLEGAL). This
* is the regression guard for the consumer-grant bug class: NATS only wildcards
* a WHOLE token, so an embedded `*` (e.g. `S_acme_*`) is a LITERAL and matches
* NOTHING — every consumer-bind against such a grant fails with a Permissions
* Violation. Empirically proven against a real nats-server. Throws on any
* embedded wildcard so this bug fails a TEST, never prod. Exported so callers
* (and the regression test) can assert their derived subjects are legal.
*/
function assertNatsGrantTokensLegal(subject) {
	for (const token of subject.split(".")) {
		if (token === "*" || token === ">") continue;
		if (token.includes("*") || token.includes(">")) throw new Error(`Illegal NATS subject ${JSON.stringify(subject)}: token ${JSON.stringify(token)} has an embedded '*'/'>' — NATS only wildcards a WHOLE token, so an embedded wildcard is a literal that matches nothing.`);
	}
}
/**
* The fixed set of platform COORDINATION KV buckets (dynamic-agent-streams G3b)
* the per-DP SHARED services use: agent leases + the two leader-election locks.
* SINGLE SOURCE OF TRUTH for BOTH the operator's static KV provisioner
* (`kv-reconciler` `STATIC_COORDINATION_KV_SPECS`) and the svc NATS grant
* ({@link coordinationKvGrantSubjects}) — so the buckets PROVISIONED and the
* buckets GRANTED can never drift.
*/
const COORDINATION_KV_BUCKETS = {
	agentLeases: "AGENT_LEASES",
	eventServerLeader: "event-server-leader",
	s3ArchiverLeader: "s3-archiver-leader",
	agentRevocations: "AGENT_REVOCATIONS",
	auditArchiverLeader: "audit-archiver-leader"
};
/** The coordination bucket NAMES (declaration order). */
const COORDINATION_KV_BUCKET_NAMES = Object.values(COORDINATION_KV_BUCKETS);
/**
* The coordination buckets a per-DP SHARED service (`svc`) co-owns and holds a full KV-client (READ+WRITE)
* grant on — leases + the two leader locks. Deliberately EXCLUDES `agentRevocations`: the shared services
* never read/write the kill-switch bucket; its READ goes to the unit identity
* ({@link agentRevocationsKvReadGrantSubjects}) and its WRITE to the Directory / break-glass (minted by
* WS-2/WS-3, not derived here). Value = the pre-agentRevocations `COORDINATION_KV_BUCKET_NAMES`, so the svc
* grant is UNCHANGED. This is the DEFAULT for {@link coordinationKvGrantSubjects} so a bare call can never
* hand a `svc` principal AGENT_REVOCATIONS write.
*/
const COORDINATION_KV_RW_BUCKET_NAMES = [
	COORDINATION_KV_BUCKETS.agentLeases,
	COORDINATION_KV_BUCKETS.eventServerLeader,
	COORDINATION_KV_BUCKETS.s3ArchiverLeader
];
/**
* The KV-CLIENT subject grant a per-DP SHARED service (`svc`) needs to USE the
* coordination KV buckets (leases + leader-election locks). A JetStream KV bucket
* `B` is a backing stream `KV_<B>`; a client (bind + get + put/CAS + watch) touches:
*   - `$JS.API.STREAM.INFO.KV_<B>`             — bind (read the bucket's config)
*   - `$JS.API.DIRECT.GET.KV_<B>` (+ `.>`)     — get (allow_direct: body- AND subject-form)
*   - `$JS.API.STREAM.MSG.GET.KV_<B>`          — get (non-direct fallback)
*   - `$KV.<B>.>`                              — put / create / update (CAS) / delete
* The watch/keys consumer-API subjects (`$JS.API.CONSUMER.CREATE.KV_<B>.>` …) are
* ALREADY covered by the svc standalone `*` consumer grant
* (`$JS.API.CONSUMER.CREATE.*.>` matches `KV_<B>` as a whole token) — NOT re-emitted.
*
* NARROW READ-ONLY CARVE-OUT to the HR-1 "apps never hold `$JS.API.STREAM.*`" rule
* (see {@link agentConsumerGrantSubjects}): every `STREAM.*` subject here is a READ
* (`INFO` / `MSG.GET` — never `CREATE`/`UPDATE`/`DELETE`/`PURGE`, so a svc still cannot
* AUTHOR a stream) AND scoped to the CONCRETE coordination backing-stream names (no
* wildcard stream token). `svc`-ONLY (the shared services co-own these buckets;
* per-DP NATS-account isolation is the trust boundary). Every subject is
* whole-token-legal by construction ({@link assertNatsGrantTokensLegal}).
*
* Parameterized per-bucket (issue #1, D5; forward-compatible with the D4 per-bucket
* confinement follow-up) — emits ONLY the per-bucket KV-client subjects for `buckets`,
* `[]` when `buckets` is empty. `$JS.API.INFO` is **deliberately NOT** emitted here: it
* belongs to the app-level (P3) variant {@link appCoordinationKvGrantSubjects} only, so the
* `svc` grant that ALSO consumes this helper is NOT broadened (audit point 1, N-1/N-4).
*
* `buckets` **defaults to {@link COORDINATION_KV_RW_BUCKET_NAMES}** (the r/w coordination buckets —
* leases + the 2 leader locks), which **EXCLUDES `agentRevocations`** (SEC-4, CONFIRM-4/D17.4): a bare
* 0-arg call can NEVER hand a `svc` principal AGENT_REVOCATIONS write. This is byte-identical to the
* pre-AGENT_REVOCATIONS `COORDINATION_KV_BUCKET_NAMES` (that constant had only these 3 buckets), so the
* `svc` branch / a no-arg caller gets an UNCHANGED grant; a caller passing an explicit subset gets the
* narrow per-bucket grant. Adding the optional parameter is additive (no breaking API change).
*
* `qualified` (D14, default `true`) selects the JS-API subject prefix. LEAF clients (the app-level
* P3 cred via {@link appCoordinationKvGrantSubjects}, the default) get the domain-qualified
* `$JS.hub.API.*`. The DIRECT svc grant ({@link agentConsumerGrantSubjects} svc branch) passes
* `false` → PLAIN `$JS.API.*` (a direct/no-`apiPrefix` client addresses KV via plain `$JS.API`),
* and the prefixed `$JS.hub.API.$KV.*` form is omitted (a direct client never prefixes `$KV` ops).
* Defaulting to `true` keeps the 0-/1-arg signatures byte-identical (back-compat preserved).
*/
function coordinationKvGrantSubjects(buckets = COORDINATION_KV_RW_BUCKET_NAMES, qualified = true) {
	const api = qualified ? jsApi : jsApiPlain;
	const out = [];
	for (const bucket of buckets) {
		const backing = `KV_${bucket}`;
		out.push(api(`STREAM.INFO.${backing}`), api(`DIRECT.GET.${backing}`), api(`DIRECT.GET.${backing}.>`), api(`STREAM.MSG.GET.${backing}`), `$KV.${bucket}.>`);
		if (qualified) out.push(jsApi(`$KV.${bucket}.>`));
	}
	return out;
}
/**
* The per-AGENT lease-KV grant (incident 2026-07-02 — per-EC creds, BRK-1/B1): the
* MINIMAL subject subset an execution-context needs to hold its ONE-MIND lease in the
* `AGENT_LEASES` coordination bucket when connecting with its OWN per-EC cred. Injected
* into the platform-minted consumer grant for kind `agent` ONLY (never `human` —
* no lease; never `svc` — the svc coordination grant already covers the buckets).
*
* Shape (the agent client always runs with the leaf `apiPrefix` — `$JS.hub.API` — so the
* API subjects are QUALIFIED, matching the agent consumer-grant convention; and per D7,
* nats.js PREFIXES the `$KV` data subject when an `apiPrefix` is set, so BOTH own-key
* forms are granted):
*   - `$JS.hub.API.INFO`                              — the `jetstreamManager()` account probe
*   - `$JS.hub.API.STREAM.INFO.KV_AGENT_LEASES`       — bucket bind/assert (`streams.info`)
*   - `$JS.hub.API.DIRECT.GET.KV_AGENT_LEASES(.>)`    — lease reads (direct)
*   - `$JS.hub.API.STREAM.MSG.GET.KV_AGENT_LEASES`    — lease reads (non-direct)
*   - `$KV.AGENT_LEASES.<orgId>.<principalId>` (+ the `$JS.hub.API.`-prefixed form)
*     — the OWN-key write ONLY (B1: `leaseKey = <orgId>.<principalId>`); NEVER
*     `$KV.AGENT_LEASES.>` — a cross-agent lease WRITE would let a co-resident EC
*     overwrite another agent's lease value, whose KV revision IS the one-mind fencing
*     epoch (Law-1 subversion at the broker).
*
* DOCUMENTED RESIDUAL (mirrors the app-side analysis): the two READ subjects are
* STREAM-level (the key rides the request body; NATS subject perms cannot scope them
* per-key), so any EC can READ any agent's lease value — confidentiality-only, not
* fencing-integrity.
*/
function agentLeaseKvGrantSubjects(orgId, principalId) {
	agentStreamName(orgId, principalId);
	return [...compileStandingGrants("agent-lease", {
		orgId,
		principalId
	}).publish];
}
/**
* The READ-ONLY KV-client subject subset the UNIT identity's static cred needs to BIND + GET the
* `AGENT_REVOCATIONS` bucket (SEC-4). Mirrors the READ subjects of {@link agentLeaseKvGrantSubjects}
* (DUAL-dialect: qualified `$JS.hub.API.*` + plain `$JS.API.*`, via {@link dualJsApi}) MINUS any
* `$KV.<bucket>` WRITE — a unit may GET a marker but NEVER PUT/DEL (it cannot un-revoke nor forge-revoke
* itself; WRITE is Directory + break-glass only, minted by WS-2/WS-3). READ subjects are STREAM-level (the
* key rides the request body; NATS subject perms cannot scope per-key) — the documented residual (accepted
* same-org DoS, D3). `$JS.API.INFO` is NOT emitted — it rides the unit's lease grant
* ({@link orgScopedLeaseKvGrantSubjects}, which every unit also holds). Whole-token-legal by construction
* ({@link assertNatsGrantTokensLegal}). Called no-arg by WS-3's `platformMintedUnitGrant`.
*/
function agentRevocationsKvReadGrantSubjects() {
	return [...compileStandingGrants("unit-revocations", {}).publish];
}
/**
* The ORG-SCOPED lease-KV grant the UNIT identity's static cred holds (D3): READ+WRITE across the unit's
* whole org lease subtree `$KV.AGENT_LEASES.<orgId>.>` — NOT own-key (a unit runs many teams' one-mind
* lease logic in-process; a malicious same-org unit stomping a sibling lease = the ACCEPTED same-org DoS,
* D3). The org-wildcard analogue of {@link agentLeaseKvGrantSubjects}: SAME dual-dialect read-API subjects
* + `$JS.API.INFO` on `KV_AGENT_LEASES`, but the WRITABLE data subject is `$KV.AGENT_LEASES.<orgId>.>`
* (org-wide) instead of the own-key `<orgId>.<principalId>`. Both `$KV` dialects (plain + D7
* apiPrefix-prefixed). `orgId` is asserted token-legal (it rides the `$KV` subject — fail-closed injection
* guard). Whole-token-legal by construction. Called by WS-3's `platformMintedUnitGrant(orgId)`.
*/
function orgScopedLeaseKvGrantSubjects(orgId) {
	assertValidStreamOrgId(orgId);
	return [...compileStandingGrants("unit-lease", { orgId }).publish];
}
/**
* The ORG-TRANSPORT NATS subject the UNIT identity's static org-scoped cred needs to PUB/SUB its org
* event bus (SDK-SURFACE §2 "org streams"; D3; D20/D23). A compute unit is single-org and hosts many
* teams, so it holds the WHOLE org subtree `org.<orgId>.>` — the org-wide transport analogue of the
* per-principal {@link agentPublishSubjects}/{@link agentSubscribeSubjects} (which scope to one
* principal's subtree). Returns the ONE org-wide transport subject; WS-3's `platformMintedUnitGrant`
* unions it into BOTH `publish` AND `subscribe` (the "pub/sub" dual) so the unit can emit AND receive
* org events. This is PLAIN core-NATS pub/sub — NOT a `$JS` API subject — so there is no dialect split
* (unlike {@link agentRevocationsKvReadGrantSubjects}/{@link orgScopedLeaseKvGrantSubjects}). Whole-org
* reach is the accepted org-grain model (D2/D9 — platform authorizes at org grain; per-team/agent
* authz is app-layer; same-org DoS accepted, D3). `orgId` is asserted token-legal (it rides the
* subject — fail-closed injection guard) so a `.`/`*`/`>` can never be smuggled into the subtree.
* MUST be keyed on the DP-LOCAL `auth.organizations.id` WS-3 resolves (D20), NOT the CP org id.
*/
function orgTransportGrantSubjects(orgId) {
	assertValidStreamOrgId(orgId);
	return [`org.${orgId}.>`];
}
/**
* The app-level (P3 event-bus cred) coordination-KV grant: the per-bucket KV-client subjects
* (see {@link coordinationKvGrantSubjects}) PLUS `$JS.API.INFO`. The P3 cred carries neither the
* worker `checkAPI:false` skip nor `$JS.API.>`, so the JetStream `kv()` bind's account-info probe
* needs an explicit `$JS.API.INFO` grant (live: `agent-directory: PERMISSIONS_VIOLATION publish
* $JS.API.INFO`). Emitted ONCE, account-wide; read-only account info (D5, low-risk). `[]` when no
* bucket is requested (undeclared app ⇒ byte-identical-to-today P3 cred). SINGLE SOURCE for BOTH
* the operator P3 mint and core-api's P3 scope-bound (D6 lockstep). NOT consumed by the svc grant
* — `coordinationKvGrantSubjects` is, so the svc cred is unchanged (audit point 1, N-1/N-4).
*/
function appCoordinationKvGrantSubjects(buckets) {
	if (buckets.length === 0) return [];
	return [jsApi("INFO"), ...coordinationKvGrantSubjects(buckets)];
}
/**
* The cross-fixture consumer-API grant a P3 app cred needs to bind pull consumers on a SIBLING
* fixture's per-principal stream(s) within the SAME org (N-2). For each declared sibling
* `principalId` it emits the DUAL-dialect CONCRETE consumer-API subjects for the full-literal
* `S_<org>_<principalId>` stream token (matches exactly that one stream): the QUALIFIED
* `$JS.hub.API.*` set PLUS the PLAIN `$JS.API.*` set: `nats_stream:read` on the sibling's
* principal stream in the `dual` dialect, compiled by `compileNatsGrantSubjects` (`stream-verbs.ts`).
*
* DUAL-dialect (incident 2026-07-02 rationale, extended here 2026-07-23): a COMPUTE UNIT consumes a
* sibling's stream (e.g. the `agent-service` binding `agentsvc-rediscovery` on the directory's
* `S_<org>_directory`) DIRECT-to-hub — it is leaf-EXCLUDED when its fixture declares
* `consumesAllStreams` ({@link ZION_NATS_JS_API_PREFIX_ENV} UNSET) — so nats.js emits PLAIN
* `$JS.API.*` for the `CONSUMER.DURABLE.CREATE` publish and a qualified-only grant DENIES it
* (`Permissions Violation for Publish to "$JS.API.CONSUMER.DURABLE.CREATE.S_<org>_directory.…"`). A
* LEAF-connected consumer emits the qualified `$JS.hub.API.*` form. Granting BOTH is same-resource,
* both-routing-form (the hub serves both) — it makes the sibling grant robust to the consumer's
* connection dialect, EXACTLY mirroring the agent own-stream dual ({@link agentConsumerGrantSubjects}
* `kind==='agent'`, whose standing `agent-stream` shape compiles the same `dual` consume).
* `$JS.ACK.<stream>.>` is domain-INDEPENDENT (emitted ONCE by the qualified set) — NOT repeated in
* the plain set.
*
* NEVER an `S_<org>_*` embedded wildcard (a NATS literal that matches nothing) and NEVER a standalone
* `*` (that is the `svc`-only ALL-streams grant — {@link agentConsumerGrantSubjects}); every subject
* is whole-token-legal by construction (verifiable via {@link assertNatsGrantTokensLegal}). `[]` when
* `principalIds` is empty.
*
* `orgId` is the principal's DP-LOCAL org (`auth.organizations.id`) — the id the stream is actually
* created under, NOT the CP org id. core-api resolves it authoritatively at mint and is the SOLE
* caller that supplies a concrete `orgId` here; the operator never resolves an org, so it cannot widen
* the grant to another org's stream (the H2.b-sec property). `agentStreamName` asserts both ids
* token-legal (fail-closed) before the name is built.
*/
function appSiblingStreamConsumerGrantSubjects(orgId, principalIds) {
	const out = [];
	for (const principalId of principalIds) {
		const grant = compileNatsGrantSubjects("nats_stream", {
			type: "principal",
			slug: principalId
		}, "read", {
			orgId,
			confinement: "org-grant",
			dialect: "dual"
		});
		out.push(...grant.publish);
	}
	return out;
}
/**
* The EXACT-MATCH JetStream consumer-API grant a per-principal connection needs
* to operate pull consumers against the per-principal streams (dynamic-agent-
* streams §4), keyed by the principal's {@link AgentStreamKind}. The platform
* MINTS this grant at cred-issue (core-api enroll) — it is NOT taken on trust
* from the requester. Two shapes, by kind:
*
*   - `svc` — the per-DP SHARED, active-active SERVICES (event-server /
*     archiver) legitimately consume ALL `S_<org>_*` streams in the DP, so they
*     receive STANDALONE full-token `*` consumer grants
*     (`$JS.API.CONSUMER.CREATE.*.>`, …). Each `*` is a COMPLETE token (legal —
*     it matches every stream name); isolation is the per-DP NATS account plus
*     the hard kind-gate in core-api (only a `svc` principal may ever receive
*     this `*` grant). `principalId` is unused for `svc`.
*   - `agent` / `human` — the principal receives a CONCRETE own-stream
*     grant against its literal stream name `agentStreamName(orgId, principalId)`
*     (= `S_<org>_<principalId>`, a full literal token that matches that one
*     stream exactly). NO wildcard — the principal can operate consumers ONLY on
*     its own stream.
*
* The OLD `S_<org>_*` / `V_<org>_*` EMBEDDED-wildcard globs are GONE: an
* embedded `*` inside a token is a NATS literal that matches nothing (proven
* against a real nats-server). Per-team concrete `V_` grants are a Phase-2
* concern (when `V_` streams exist) — no broken `V_` wildcard is emitted.
*
* DELIBERATELY ABSENT: any `$JS.API.STREAM.*` — apps NEVER hold stream-admin
* (the operator is the sole stream author, HR-1). Every emitted subject is
* whole-token-legal by construction ({@link assertNatsGrantTokensLegal}).
*/
function agentConsumerGrantSubjects(orgId, kind, principalId) {
	assertValidStreamOrgId(orgId);
	if (kind === "svc") return [...compileStandingGrants("svc", { orgId }).publish];
	if (kind === "agent") return [...compileStandingGrants("agent-stream", {
		orgId,
		principalId
	}).publish, ...agentLeaseKvGrantSubjects(orgId, principalId)];
	return [...compileStandingGrants("human", {
		orgId,
		principalId
	}).publish];
}
/**
* M6 (self-healing-reconciler §4) — the NATS GRANT CONTRACT VERSION. Bump this
* STRING whenever the SHAPE of ANY server-injected NATS grant helper changes:
*   - the verb families of `stream-verbs.ts` and the standing grants of `nats-standing-grants.ts`
*     (the shared core, exercised through {@link agentConsumerGrantSubjects},
*     {@link appSiblingStreamConsumerGrantSubjects} and the KV helpers below)
*   - {@link agentConsumerGrantSubjects}
*   - {@link agentLeaseKvGrantSubjects}
*   - {@link agentPublishSubjects}
*   - {@link agentSubscribeSubjects}
*   - {@link appSiblingStreamConsumerGrantSubjects}
*   - {@link appJetStreamGrantSubjects}
*   - {@link agentRevocationsKvReadGrantSubjects}
*   - {@link orgScopedLeaseKvGrantSubjects}
*   - {@link orgTransportGrantSubjects}
*
* The operator folds this version into `appNatsPermissionsHash` (and the per-svc
* cred reconciler's hash), so a bump DRIFTS the hash → every dependent cred
* auto-re-mints on the next ~30s reconcile cycle (D9 self-heal — no manual
* `delete secret` + operator restart). A `nats-grant-contract-version.test.ts`
* CONFORMANCE guard snapshots the OUTPUT of those helpers next to this constant:
* a helper-shape change without bumping this version FAILS that test.
*
* Format: `YYYY-MM-DD.N` (date of the change + a same-day counter). Bumped to
* `2026-07-01.1` for the standalone `STREAM.INFO.*` addition to the svc branch of
* {@link agentConsumerGrantSubjects} — the shared event-server needs STREAM.INFO
* on the `*` all-streams grant to bind (ordered-consume) every per-principal
* `S_<org>_<id>` stream (previously `2026-06-30.1` for the concrete-target
* STREAM.INFO addition to `consumerApiSubjectsFor`, now the `nats_stream:read` verb of `stream-verbs.ts`; commit `1c49f40bb`).
* Bumped `2026-07-03.1` → `2026-07-17.1` for the AGENT_REVOCATIONS read grant
* ({@link agentRevocationsKvReadGrantSubjects}) + {@link orgScopedLeaseKvGrantSubjects}, and the
* coordination r/w-default narrowing to {@link COORDINATION_KV_RW_BUCKET_NAMES} (EXCLUDES
* AGENT_REVOCATIONS). Bumped `2026-07-17.1` → `2026-07-17.2` (round-1 R-B/D23) for the NEW
* {@link orgTransportGrantSubjects} helper the unit grant now composes; drifts appNatsPermissionsHash so
* dependent creds re-mint. Bumped `2026-07-17.2` -> `2026-07-23.1` (CU<->static NATS sibling-stream
* grant parity) — {@link appSiblingStreamConsumerGrantSubjects} now emits the DUAL-dialect sibling
* grant (qualified `$JS.hub.API.*` + plain `$JS.API.*`) so a direct-to-hub compute-unit consumer binds
* a sibling's stream, mirroring the agent own-stream dual; drifts appNatsPermissionsHash so every
* dependent app cred (any app declaring `consumesStreams`/`consumesAllStreams`) auto-re-mints on the
* next reconcile cycle.
*/
const NATS_GRANT_CONTRACT_VERSION = "2026-07-23.1";
const WORKER_CONSUMER_CONFIG = {
	ack_policy: "explicit",
	ack_wait_s: 60,
	max_deliver: 5,
	/**
	* ⛔ THIS IS THE **WORKER** CEILING AND IT NEVER REACHES A WORK TARGET. It was the only bare
	* literal in this object, and that silence cost real time on 2026-08-27: a live work durable
	* showed `max_ack_pending: 10`, this constant was read as its source, and a fleet-wide raise was
	* nearly applied to fix one app's throughput.
	*
	* ⇒ `workTargetConsumerConfig` ALWAYS overrides this with the target's own `concurrency`, which
	* the manifest schema makes **required**, gives **no default**, and bounds at **1000**. So a work
	* target's ceiling is always its own declaration; a `10` observed on a work durable is the app
	* asking for 10, never this value leaking.
	*
	* ⚠️ Changing this number changes every NATS WORKER's in-flight ceiling across the fleet, and
	* nothing about work targets. Those are different questions with different blast radii; do not
	* answer one by editing the other.
	*/
	max_ack_pending: 10,
	/**
	* ⛔ FOUR VALUES FOR FIVE DELIVERIES, AND THE ARITHMETIC IS THE POINT. `max_deliver: 5` means one
	* delivery plus FOUR redeliveries, so only four backoffs can ever be applied. A fifth entry is
	* unreachable by construction — and NATS REJECTS the consumer outright rather than ignoring it:
	* *"max deliver is required to be > length of backoff values"*.
	*
	* ⚠️ This was `[10, 30, 60, 300, 600]` — five entries against five deliveries — and it is a
	* PRE-EXISTING defect, not a change of policy: the fifth value never applied, so removing it
	* alters no retry timing. It surfaced only when a new consumer was created against a real broker
	* for the first time; every already-existing durable was created before anything validated this,
	* so the fleet shows no symptom while every NEW consumer using this config would be refused.
	*/
	/**
	* ⛔⛔ `backoff_s[0]` IS THE EFFECTIVE `ack_wait`. JetStream PINS `ack_wait` to `backoff[0]` on
	* any consumer that declares `backoff`, and a write to `ack_wait` SUCCEEDS, echoes the OLD value
	* back, and changes nothing. So this array's HEAD, not `ack_wait_s` above, is the ack deadline
	* the broker enforces.
	*
	* MEASURED 2026-08-27 on live `dp-app`, three arms on throwaway consumers:
	*   `backoff [10s,30s]`  + set `ack_wait=60s` -> stays **10s** (pinned)
	*   NO backoff           + set `ack_wait=60s` -> becomes **60s** (control: the write works)
	*   `backoff [60s,300s]` + set `ack_wait=60s` -> is **60s** (equals `backoff[0]`)
	*
	* ⛔ THIS HEAD WAS `10` AGAINST AN `ack_wait_s` OF 60, AND THE BROKER USED 10. Every worker
	* durable in the fleet ran a 10-second ack deadline against the 20-second extension heartbeat, so
	* every `extendAck()` arrived AFTER the redelivery it exists to prevent and `max_deliver: 5` let
	* one tenant payload execute up to five times. Observed as `redeliv=1` on `zion-build`'s durable.
	*
	* ⛔ AND IT DEFEATED THE GUARD BUILT FOR EXACTLY THIS. `workAttemptLeaseMs` reasons *"let
	* W = ack_wait"* and the operator refuses at boot when `W - R <= 0`. Fed the DECLARED 60 it
	* computed a comfortable 40 000; fed the EFFECTIVE 10 it is **-10 000**, which would have refused
	* boot on day one. A coherence check pointed at the number the broker ignores cannot fail.
	*
	* The head moved and the tail did not. When the head moved, the operator config refused boot
	* unless `abandonedLeaseHorizonSeconds` (default 600) exceeded `max(backoff_s)`, so a tail of 600
	* would have refused boot. Plan 2026-10-14-app-pools-runtime-deletion deleted that horizon and its
	* boot refusal (stage S9, R-069 item 5), so no live reader of the maximum remains. Max stays 300,
	* the array stays monotonic, and it stays four entries for five deliveries. Tests in
	* `event-subjects.test.ts` pin the three facts.
	*
	* ⚠️ Retry timing genuinely changes: the first redelivery of a failed message now waits 60 s
	* rather than 10 s. For work runs measured in minutes that is the point — a 10-second first retry
	* on a two-minute job is what produced the duplicates.
	*/
	backoff_s: [
		60,
		120,
		240,
		300
	],
	/**
	* 31 days — deliberately LONGER than the longest finite stream retention in the
	* fleet (30d on 35 streams; 14d on 276), so a reap can only ever land on a
	* durable whose stream has already aged out everything it had not consumed.
	*
	* ⛔ DO NOT shorten this to "bound the leak faster" without checking the reap
	* COST at the call site. These durables default to `deliver_policy: all`, so a
	* premature reap restarts at sequence 1 and replays up to the stream's full
	* retention — re-processing work that was already done. The purpose here is to
	* convert an UNBOUNDED lifetime into a bounded one, not to reclaim aggressively.
	*
	* ⚠️ Two streams on `dp-app` carry `max_age: 0` (unbounded), for which no finite
	* threshold is provably lossless — a call site consuming one of those must pick
	* its own value with its handler's idempotency in mind.
	*/
	inactive_threshold_s: 2678400
};
/** The per-DP scaling-event JetStream stream name (operator-authored). */
const SCALING_STREAM_NAME = "ZION_SCALING";
/** The subject subtree the ZION_SCALING stream captures. */
const SCALING_SUBJECT_ROOT = "zion.scaling";
/**
* The reserved app-slug token embedded in {@link SCALING_SUBJECT_ROOT}
* (`zion.` + `scaling`). Added to the reserved-app-slug denylist (§2.7 b) so an
* app can never be slugged `scaling` and thereby be granted `zion.scaling.>`.
*/
const SCALING_RESERVED_SLUG = "scaling";
/**
* PLATFORM-RESERVED APP SCOPES (DEC-S4-d-READLEG, security B1-R2) — the SINGLE
* source of truth for every token that must never be an app scope/slug, because
* an app holding it would be auto-granted a PLATFORM stream's subjects and/or
* its JetStream consumer-API family:
*   - `scaling`  → `zion.scaling.>` + `$JS.…CONSUMER.*.ZION_SCALING.>` (this WS)
*   - `dlq`      → `zion.dlq.>`     + `$JS.…CONSUMER.*.ZION_DLQ.>`
*   - `core-api` → the `ZION_CORE-API` system stream
*   - `audit`    → `zion.audit.>`   + the `ZION_AUDIT` platform stream (audit-sink R-014)
*   - `satellite` → `zion.satellite.>`, the core-NATS wire between a data plane's home operator
*     and its satellite operators (`zion.satellite.<dataPlaneId>.<region>.run.v1`, the per-run
*     events and the status subject; 2026-09-29-gpu-and-regional-app-pools R-016). An app slugged
*     `satellite` would be minted `zion.satellite.>` and could answer or read another data
*     plane's regional runs. No JetStream stream sits under this root today.
*
* Consumed by ALL FOUR enforcement points so they can never drift apart:
*   (a) `isNatsSubjectWithinAppScope` — scope-level fail-closed (§2.7 a);
*   (b) `createAppSchema` — BOTH copies, `@zion/sdk-contracts` SoT + the
*       release-engine DTO (§2.7 b);
*   (c) release-engine `computeDesiredState`'s reserved-app-id stream skip,
*       which today hard-codes `'dlq'`/`'core-api'` (§2.7 c);
*   (d) the deploy-time pre-existing-slug preflight (§2.7 d).
*
* `SCALING_RESERVED_SLUG` is a member by identifier; the others are literals, which
* `cloud/cp/bin/lib/reserved-app-slug-preflight.sh` restates in its SQL array and
* `reserved-app-slug-preflight.test.sh` compares against this block. The stream
* composer in release-engine's `OperatorApiService.computeDesiredState()` skips
* every member.
*/
const RESERVED_APP_SCOPES = /* @__PURE__ */ new Set([
	SCALING_RESERVED_SLUG,
	"dlq",
	"core-api",
	"audit",
	"satellite"
]);
/** App-scope token guard: NATS-legal single token (no `.`/`*`/`>`/whitespace), 1–63 chars. */
const SCALING_APP_SCOPE_PATTERN = /^[a-zA-Z0-9_-]{1,63}$/;
/**
* Assert `appScope` is a legal scaling subject token. Fail-closed — the value is
* a k8s label (`zion.witness.ai/app`), so a malformed value throws rather than
* producing a wildcard-smuggling subject.
*/
function assertValidScalingAppScope(appScope) {
	if (!SCALING_APP_SCOPE_PATTERN.test(appScope)) throw new Error(`Invalid scaling appScope ${JSON.stringify(appScope)}: must match /^[a-zA-Z0-9_-]{1,63}$/.`);
}
/**
* The version-suffixed PUBLISH subject an operator scaling event is emitted on:
* `zion.scaling.<appScope>.<class>.<version>`. `version` is sanitised
* ({@link sanitizeForDurableName} — dot-free) so a dotted versionSlug cannot add
* subject tokens; `class` is a fixed enum value. Whole-token-legal by
* construction ({@link assertNatsGrantTokensLegal}).
*/
function scalingPublishSubject(appScope, klass, version) {
	assertValidScalingAppScope(appScope);
	const subject = `${SCALING_SUBJECT_ROOT}.${appScope}.${klass}.${sanitizeForDurableName(version)}`;
	assertNatsGrantTokensLegal(subject);
	return subject;
}
/** The per-APP consumer filter: `zion.scaling.<appScope>.>` (one app's events). */
function scalingAppFilterSubject(appScope) {
	assertValidScalingAppScope(appScope);
	return `${SCALING_SUBJECT_ROOT}.${appScope}.>`;
}
/** The DP-WIDE consumer filter: `zion.scaling.>` (all apps — admin/site-admin read). */
function scalingDpFilterSubject() {
	return `${SCALING_SUBJECT_ROOT}.>`;
}
//#endregion
export { AGENT_STREAM_KINDS, AGENT_STREAM_RETENTION_POLICY, COORDINATION_KV_BUCKETS, COORDINATION_KV_BUCKET_NAMES, COORDINATION_KV_RW_BUCKET_NAMES, LIBRARY_INBOUND_SUBJECT_TOKEN, LIBRARY_OUTBOUND_SUBJECT_TOKEN, NATS_GRANT_CONTRACT_VERSION, NATS_JS_API_DOMAIN, NATS_JS_API_PREFIX, RESERVED_APP_SCOPES, SCALING_RESERVED_SLUG, SCALING_STREAM_NAME, SCALING_SUBJECT_ROOT, STREAM_PRINCIPAL_ID_PATTERN, STREAM_PRINCIPAL_ID_TOKEN_PATTERN, WORKER_CONSUMER_CONFIG, ZION_NATS_HUB_URL_ENV, ZION_NATS_JS_API_PREFIX_ENV, agentConsumerGrantSubjects, agentLeaseKvGrantSubjects, agentPublishSubjects, agentRevocationsKvReadGrantSubjects, agentStreamName, agentStreamSubjects, agentSubscribeSubjects, appCoordinationKvGrantSubjects, appJetStreamGrantSubjects, appSiblingStreamConsumerGrantSubjects, appTopicRefusal, assertNatsGrantTokensLegal, assertValidDurableName, assertValidScalingAppScope, assertValidStreamOrgId, assertValidStreamPrincipalId, assertValidTopic, clampRetention, commandDurableName, commandFilterSubject, commandSubject, coordinationKvGrantSubjects, dualJsApi, eventPublishSubject, eventStreamName, isAgentStreamKind, isValidStreamPrincipalId, jsApi, jsApiPlain, libraryInboundFilterSubject, libraryInboundPublishSubject, libraryOutboundFilterSubject, libraryOutboundPublishSubject, orgScopedLeaseKvGrantSubjects, orgTransportGrantSubjects, parseLibraryOutboundBindingTopic, perInstanceKeyRefusal, rpcFilterSubject, rpcSubject, sanitizeForDurableName, scalingAppFilterSubject, scalingDpFilterSubject, scalingPublishSubject, workerDurableName, workerFilterSubject };

//# sourceMappingURL=event-subjects.js.map